/**
 * Hooks de la messagerie (docs/13 §4).
 *
 * ⚠️ **Le corps d'un message supprimé vaut `null`, jamais la chaîne vide.**
 * Le serveur envoie l'**état** (`is_deleted`), le client choisit les mots — et
 * les traduit (règle 10). Un serveur qui renverrait « Message supprimé » figerait
 * la phrase en français dans une interface passée à l'anglais.
 *
 * Toute écriture invalide `messagingKeys.all` : un message change la liste, le
 * fil et le badge, et les invalider séparément laisserait l'un des trois mentir.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, unwrap } from "@/shared/api/client";
import type { ApiError } from "@/shared/api/errors";
import type { components } from "@/shared/api/generated";
import { messagingKeys } from "@/shared/api/query-keys";

export type Conversation = components["schemas"]["ConversationResponse"];
export type ConversationPage = components["schemas"]["ConversationListResponse"];
export type Message = components["schemas"]["MessageResponse"];
export type MessagePage = components["schemas"]["MessageListResponse"];
export type ReportReason = components["schemas"]["ReportReason"];

export function useConversations(
  includeArchived = false,
): UseQueryResult<ConversationPage, ApiError> {
  return useQuery({
    queryKey: messagingKeys.list(includeArchived),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/conversations", {
          params: { query: { include_archived: includeArchived } },
        }),
      ),
  });
}

export function useConversation(conversationId: string): UseQueryResult<Conversation, ApiError> {
  return useQuery({
    queryKey: messagingKeys.conversation(conversationId),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/conversations/{conversation_id}", {
          params: { path: { conversation_id: conversationId } },
        }),
      ),
  });
}

/**
 * L'historique, éventuellement filtré par une recherche (docs/13 §1).
 *
 * `search` vide veut dire « pas de recherche » et non « recherche qui ne trouve
 * rien » : le paramètre n'est alors pas envoyé, et le serveur rend le fil. La
 * distinction est faite ici plutôt que dans l'écran, pour que les deux appelants
 * qu'aura un jour cette fonction ne la refassent pas chacun de son côté.
 */
export function useMessages(
  conversationId: string,
  search = "",
): UseQueryResult<MessagePage, ApiError> {
  const trimmed = search.trim();
  return useQuery({
    queryKey: messagingKeys.messages(conversationId, trimmed),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/conversations/{conversation_id}/messages", {
          params: {
            path: { conversation_id: conversationId },
            query: trimmed === "" ? {} : { q: trimmed },
          },
        }),
      ),
    // Garde le fil précédent à l'écran pendant que la recherche suivante
    // arrive : sans lui, chaque lettre tapée vide la liste puis la remplit, et
    // l'écran clignote au rythme de la frappe.
    placeholderData: (previous) => previous,
  });
}

export function useUnreadMessageCount(): UseQueryResult<number, ApiError> {
  return useQuery({
    queryKey: messagingKeys.unreadCount(),
    queryFn: async () => (await unwrap(api.GET("/api/v1/messages/unread-count", {}))).unread_count,
  });
}

export function useOpenConversation(): UseMutationResult<
  Conversation,
  ApiError,
  { participantId: string; courseId?: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ participantId, courseId }) =>
      unwrap(
        api.POST("/api/v1/conversations", {
          body: {
            participant_id: participantId,
            ...(courseId === undefined ? {} : { course_id: courseId }),
          },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: messagingKeys.all }),
  });
}

export function useSendMessage(
  conversationId: string,
): UseMutationResult<Message, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      unwrap(
        api.POST("/api/v1/conversations/{conversation_id}/messages", {
          params: { path: { conversation_id: conversationId } },
          body: { body },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: messagingKeys.all }),
  });
}

export function useDeleteMessage(): UseMutationResult<Message, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      unwrap(
        api.DELETE("/api/v1/messages/{message_id}", {
          params: { path: { message_id: messageId } },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: messagingKeys.all }),
  });
}

export function useMarkConversationRead(): UseMutationResult<unknown, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) =>
      unwrap(
        api.POST("/api/v1/conversations/{conversation_id}/read", {
          params: { path: { conversation_id: conversationId } },
          body: {},
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: messagingKeys.all }),
  });
}

export function useSetConversationFlag(
  flag: "archive" | "mute",
): UseMutationResult<Conversation, ApiError, { conversationId: string; enabled: boolean }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, enabled }) =>
      unwrap(
        flag === "archive"
          ? api.POST("/api/v1/conversations/{conversation_id}/archive", {
              params: { path: { conversation_id: conversationId } },
              body: { enabled },
            })
          : api.POST("/api/v1/conversations/{conversation_id}/mute", {
              params: { path: { conversation_id: conversationId } },
              body: { enabled },
            }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: messagingKeys.all }),
  });
}

export function useReportMessage(): UseMutationResult<
  unknown,
  ApiError,
  { messageId: string; reason: ReportReason }
> {
  return useMutation({
    mutationFn: ({ messageId, reason }) =>
      unwrap(
        api.POST("/api/v1/messages/{message_id}/report", {
          params: { path: { message_id: messageId } },
          body: { reason },
        }),
      ),
  });
}
